import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { AlertCircle, Lock } from 'lucide-react';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showTempPasswordWarning, setShowTempPasswordWarning] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const user = await login(email, password);
      
      if (user.has_temporary_password) {
        setShowTempPasswordWarning(true);
      }
      
      switch (user.role) {
        case 'patient':
          navigate('/dashboard');
          break;
        case 'provider':
          navigate('/provider/patients');
          break;
        case 'admin':
          navigate('/admin/dashboard');
          break;
        case 'employer':
          navigate('/employer/dashboard');
          break;
        default:
          navigate('/');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex w-full font-sans bg-white">
      {/* Left Column - Form */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-8 sm:px-16 lg:px-24 py-12 relative z-10">
        <div className="w-full max-w-md mx-auto text-center">
          {/* Logo */}
          <div className="mb-6 flex justify-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_virtualcareclinic/artifacts/5rgbdbiq_PILGRIM%20%281%29.png" 
              alt="Pilgrim Health" 
              className="h-36 object-contain"
            />
          </div>

          <div className="mb-10">
            <h1 className="text-3xl font-bold text-slate-900 mb-2 tracking-tight">Member Login</h1>
            <p className="text-slate-600">Welcome back. Your health is our priority.</p>
          </div>

          {showTempPasswordWarning && (
            <Alert className="mb-6 bg-yellow-50 border border-yellow-200">
              <Lock className="h-4 w-4 text-yellow-700" />
              <AlertDescription className="text-yellow-900">
                <strong>Security Notice:</strong> You're using a temporary password. Please change it immediately in Account Settings for your security.
              </AlertDescription>
            </Alert>
          )}

          {error && (
            <Alert variant="destructive" className="mb-6" data-testid="login-error">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-6 text-left" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700 font-medium">Email Address</Label>
              <Input
                id="email"
                type="email"
                data-testid="email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                className="h-12 border-slate-300 focus:border-blue-600 focus:ring-blue-600 rounded-lg"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-slate-700 font-medium">Password</Label>
                <Link
                  to="/forgot-password"
                  data-testid="forgot-password-link"
                  className="text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors"
                >
                  Forgot password?
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                data-testid="password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                className="h-12 border-slate-300 focus:border-blue-600 focus:ring-blue-600 rounded-lg"
              />
            </div>

            <Button
              type="submit"
              data-testid="login-submit-button"
              disabled={loading}
              className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-full transition-colors"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          {/* Privacy and Terms Links */}
          <div className="mt-12 pt-8 border-t border-slate-100">
            <div className="flex items-center justify-center gap-4 text-sm text-slate-500">
              <Link to="/privacy" className="hover:text-slate-800 transition-colors">Privacy Policy</Link>
              <span>&middot;</span>
              <Link to="/terms" className="hover:text-slate-800 transition-colors">Terms of Service</Link>
            </div>
          </div>
        </div>
      </div>

      {/* Right Column - Brand Hero Section */}
      <div className="hidden lg:flex w-1/2 bg-indigo-950 flex-col justify-center px-16 relative overflow-hidden">
        <div className="relative z-10 max-w-xl mx-auto w-full">
          <div className="mb-10 text-left">
            <h2 className="text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight leading-tight whitespace-nowrap">
              Care that feels human.
            </h2>
            <p className="text-indigo-200 text-lg leading-relaxed max-w-lg">
              We strive to keep our members happy and healthy through convenient and affordable relationship-based care.
            </p>
            <div className="mt-8">
              <a
                href="https://pilgrimhealth.io"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-6 py-3 border border-white text-white rounded-full font-medium text-sm hover:bg-white/10 transition-colors"
              >
                Learn more about Pilgrim Health
              </a>
            </div>
          </div>

          <div className="relative w-full aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl border-4 border-white/5">
            <div className="absolute inset-0 bg-indigo-950/20 mix-blend-overlay z-10"></div>
            <img 
              src="https://customer-assets.emergentagent.com/job_34f10941-b36d-4272-a35b-55dab6c64844/artifacts/nca18hbz_Gemini_Generated_Image_tt1q8dtt1q8dtt1q.png" 
              alt="Pilgrim Health consultation" 
              className="object-cover w-full h-full"
            />
          </div>
        </div>
        
        {/* Subtle background decoration */}
        <div className="absolute top-0 right-0 -mr-32 -mt-32 w-[500px] h-[500px] bg-blue-600/10 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 -ml-32 -mb-32 w-[400px] h-[400px] bg-indigo-500/10 rounded-full blur-3xl pointer-events-none"></div>
      </div>
    </div>
  );
};

export default Login;